import { Circle } from '@mui/icons-material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { ClickAwayListener } from '@mui/material';
import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Drawer } from 'src/components/drawer';
import { ActionBar, Header } from 'src/components/header';
import { IconTypography } from 'src/components/iconTypography/IconTypography';
import { Progress } from 'src/components/progress';
import { LOCATION_HEADER_HEIGHT } from 'src/constants';
import { PATHS } from 'src/constants/paths';
import { CalendarView } from 'src/features/locations/calendarLocationGroupPage/modules/CalendarView/CalendarView';
import { useModal } from 'src/hooks';
import { lang, theme } from 'src/utils';

import { DaypartDisabledAlert } from '../modules/DaypartDisabledAlert';
import { ComponentScheduleDrawer } from '../scheduleLocationGroupPage/modules/scheduleComponentDrawer/ComponentScheduleDrawer';
import { ComponentScheduleDrawerProps } from '../scheduleLocationGroupPage/modules/scheduleComponentDrawer/ComponentScheduleDrawer.interface';
import {
  ButtonGroupWrapper,
  CalendarContainer,
  Container,
  HeaderContainer,
  IconTextWrapper,
  StyledIconButton,
} from './CalendarLocationGroupPage.styled';
import { useCalendarLocationGroupPage } from './hooks/CalendarLocationGroupPage.hook';
import { openSchedulePreview } from './modules/CalendarView/eventContentSidebar/EventContentSidebar.helper';
import { DeleteBundleAction } from './modules/CalendarView/eventContentSidebar/EventContentSidebar.interface';

export const CalendarLocationGroupPage = () => {
  const CALENDAR_PATH = '/calendar';

  const sidebarDrawer = useModal();
  const location = useLocation();
  const componentScheduleDrawerRef = useRef<ComponentScheduleDrawerProps>(null);
  const navigate = useNavigate();
  const { locationGroupId = '' } = useParams();

  const isCalendarActive = location?.pathname?.includes(CALENDAR_PATH);

  //NOTE: This is set to '1' as default for Breakfast in the UI.
  const [selectedDaypartId, setSelectedDaypartId] = useState<string>('1');

  const handleSchedulePreview = () => {
    openSchedulePreview({
      locationGroupId,
      daypartId: selectedDaypartId,
    });
  };

  const {
    locationGroup,
    projectConfig,
    activeDaypartOptions,
    isLoadingActiveDayparts,
    isLoadingLocationGroup,
    deleteScheduleComponents,
    alertVisible,
    setAlertVisible,
    locationsCount,
  } = useCalendarLocationGroupPage();

  const isSelectedDaypartActive = activeDaypartOptions.some(
    (daypart) => daypart.id === Number(selectedDaypartId),
  );

  return isLoadingLocationGroup ? (
    <Progress />
  ) : (
    <Container>
      <Header
        height={LOCATION_HEADER_HEIGHT}
        title={locationGroup?.name}
        actionBar={
          <ActionBar
            disabled={!activeDaypartOptions.length}
            previewDisabled={!isSelectedDaypartActive}
            showPreview
            type={lang.scheduleComponent.label}
            onPreviewClick={handleSchedulePreview}
            onHandleClick={sidebarDrawer.onOpenModal}
          />
        }
        breadcrumbItems={[
          {
            label: lang.locationGroup,
            link: `${PATHS.LOCATION_GROUPS}`,
          },
          {
            label: lang.schedules,
          },
        ]}
      />

      <DaypartDisabledAlert
        locationGroupId={Number(locationGroupId)}
        selectedDaypartId={Number(selectedDaypartId)}
        navigateTo={PATHS.LOCATION_GROUP_TIMINGS(locationGroupId)}
        onVisibilityChange={setAlertVisible}
      />

      <HeaderContainer>
        <ButtonGroupWrapper>
          <StyledIconButton $active={!!isCalendarActive}>
            <CalendarTodayIcon />
          </StyledIconButton>
          <StyledIconButton
            onClick={() =>
              navigate({
                pathname: PATHS.LOCATION_GROUP_SCHEDULE(locationGroupId),
              })
            }>
            <FormatListBulletedIcon />
          </StyledIconButton>
        </ButtonGroupWrapper>
        <IconTextWrapper>
          <IconTypography
            icon={<Circle />}
            text={lang.default}
            textColor={theme.palette.brand.black}
            iconSize="12px"
            iconColor={theme.palette.brand.mediumGrey}
          />
          <IconTypography
            icon={<Circle />}
            text={lang.scheduled}
            textColor={theme.palette.brand.black}
            iconSize="12px"
            iconColor={theme.palette.primary.light}
          />
        </IconTextWrapper>
      </HeaderContainer>
      <CalendarContainer $alertVisible={alertVisible}>
        <CalendarView
          isCalendarViewGroup={true}
          locationGroup={locationGroup}
          projectConfig={projectConfig}
          shouldShowCalendar={
            !isLoadingActiveDayparts && activeDaypartOptions.length > 0
          }
          deleteBundleAction={deleteScheduleComponents as DeleteBundleAction}
          selectedDaypartId={selectedDaypartId}
          handleSelectedDaypartIdChange={setSelectedDaypartId}
          locationsCount={locationsCount}
        />
      </CalendarContainer>
      <ClickAwayListener
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
        onClickAway={() => {
          if (sidebarDrawer.open) {
            componentScheduleDrawerRef?.current?.onHandleCloseDrawer?.();
            sidebarDrawer.onCloseModal();
          }
        }}>
        <Drawer open={sidebarDrawer.open} width="unset">
          <ComponentScheduleDrawer
            onToggleDrawer={sidebarDrawer.onToggleModal}
            isOpen={sidebarDrawer.open}
            ref={componentScheduleDrawerRef}
            locationGroupName={locationGroup?.name}
            orientation={locationGroup?.orientation}
          />
        </Drawer>
      </ClickAwayListener>
    </Container>
  );
};
